import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Button from "@atlaskit/button/standard-button";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import AkModal, { ModalTransition, ModalHeader } from "@atlaskit/modal-dialog";

const modalContentStyles = css({
    "height": "100%",
    "width": "100%",
    "position": "relative",
    "iframe": {
        border: "none"
    },
    ".content-modal": {
        padding: "32px 16px",
        overflowY: "auto",
        maxHeight: "60vh",
        textAlign: "center",

        img: {
            "margin": "auto",
            "@media (max-width: 1024px) ": {
                height: "auto",
                maxWidth: "100%"
            }
        },
        a: {
            "color": "#0052cc",
            "textDecoration": "underline",
            "&:hover": {
                color: "#0065ff",
                textDecoration: "none"
            }
        },
        ul: {
            margin: "0 auto",
            textAlign: "left",
            width: "fit-content"
        },
        li: {
            margin: "0 0 12px"
        },
        h2: {
            "color": "#253858",
            "fontWeight": 500,
            "fontSize": "2rem",
            "lineHeight": "1.25",
            "marginBlockStart": 0,
            "marginBlockEnd": "14px",
            "@media (min-width: 768px)": {
                fontSize: "2.75rem",
                lineHeight: "1.18181818",
                marginBlockEnd: "16px"
            },
            "@media (min-width: 1440px)": {
                fontSize: "3rem",
                lineHeight: "3.5rem",
                marginBlockEnd: "24px"
            }
        }
    },

    'div[id="close-modal-button"]': {
        "position": "absolute",
        "top": "-5px",
        "right": "-60px",

        "button": {
            span: {
                color: "#ffffff"
            }
        },

        "@media screen and (max-width: 479px)": {
            "&": {
                display: "none"
            }
        }
    }
});

const closeModalStyles = css({
    "display": "flex",
    "width": "100%",
    "justify-content": "flex-end",

    "button": {
        height: "24px"
    }
});

interface ModalProps {
    isOpen: boolean;
    closeModal: () => void;
    content: (height: string) => React.ReactNode;
    closeIconLabel?: string;
    isContentModal?: boolean;
}

const DESKTOP_MODAL_SIZE = {
    width: "large", // atlaskit modal starts ignoring the width at smaller sizes
    height: "540" // atlaskit modal starts setting height to 100% at smaller sizes
};

const MOBILE_MODAL_SIZE = { width: "1024px", height: "576" };

export const Modal = ({
    isOpen,
    closeModal,
    content,
    closeIconLabel,
    isContentModal
}: ModalProps) => {
    const intl = useIntl();

    const getModalSize = () => {
        if (window.innerWidth >= 1024) {
            return MOBILE_MODAL_SIZE;
        }

        return DESKTOP_MODAL_SIZE;
    };

    const [modalSize, setModalSize] = useState(getModalSize());

    useEffect(() => {
        const updateModalSize = () => {
            setModalSize(getModalSize());
        };

        window.addEventListener("resize", updateModalSize);

        return () => window.removeEventListener("resize", updateModalSize);
    }, []);

    return (
        <ModalTransition>
            {isOpen && (
                <AkModal
                    onClose={closeModal}
                    width={modalSize.width}
                    height={modalSize.height}
                >
                    {window.innerWidth < 480 && (
                        <ModalHeader>
                            <div css={closeModalStyles}>
                                <Button
                                    appearance="subtle-link"
                                    onClick={closeModal}
                                >
                                    <CrossIcon
                                        label={
                                            closeIconLabel ??
                                            intl.formatMessage({
                                                id: "Modal.closeIconLabel",
                                                defaultMessage: "close modal"
                                            })
                                        }
                                    />
                                </Button>
                            </div>
                        </ModalHeader>
                    )}
                    <div css={modalContentStyles}>
                        <div className={isContentModal ? "content-modal" : ""}>
                            {content(getModalSize().height)}
                        </div>
                        <div id="close-modal-button">
                            <Button
                                appearance="subtle-link"
                                onClick={closeModal}
                            >
                                <CrossIcon
                                    label={
                                        closeIconLabel ??
                                        intl.formatMessage({
                                            id: "Modal.closeIconLabel",
                                            defaultMessage: "close modal"
                                        })
                                    }
                                    size="xlarge"
                                />
                            </Button>
                        </div>
                    </div>
                </AkModal>
            )}
        </ModalTransition>
    );
};
